import { useEffect, useRef, useState } from 'react';
import { useAssetUrl } from '../components/note/useAssetUrl';
import { Icon } from '../components/ui/Icon';
import { Segmented, Sheet, Switch } from '../components/ui/ui';
import { importImage, removeBackground, ImageImportError } from '../images/imageTools';
import { listAssetsMeta, putAsset, getAsset } from '../storage/repo';
import type { Asset } from '../storage/schemas';
import { store } from '../state/store';

type Tab = 'photos' | 'collections';

function Thumb({ id, onPick, label }: { id: string; onPick: () => void; label: string }) {
  const url = useAssetUrl(id);
  return (
    <button type="button" className="img-thumb" onClick={onPick} aria-label={label}>
      {url && <img src={url} alt="" loading="lazy" />}
    </button>
  );
}

/** Reference: white sheet with ✕, "Photos | Collections" segmented control and a 3-column grid. */
export function ImageSheet({ open, onClose, onAdd }: { open: boolean; onClose: () => void; onAdd: (asset: Omit<Asset, 'data'>) => void }) {
  const [tab, setTab] = useState<Tab>('photos');
  const [cutOut, setCutOut] = useState(false);
  const [busy, setBusy] = useState(false);
  const [library, setLibrary] = useState<Omit<Asset, 'data'>[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) void listAssetsMeta('image').then(setLibrary).catch(() => setLibrary([]));
  }, [open]);

  const handleFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      for (const f of [...files].slice(0, 6)) {
        let asset = await importImage(f, f.name);
        if (cutOut) asset = await removeBackground(asset);
        const saved = await putAsset(asset);
        onAdd(saved);
      }
      onClose();
    } catch (err) {
      store.toast(err instanceof ImageImportError ? err.message : `Could not add the image: ${(err as Error).message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  const pickExisting = async (meta: Omit<Asset, 'data'>) => {
    if (!cutOut) {
      onAdd(meta);
      onClose();
      return;
    }
    setBusy(true);
    try {
      const full = await getAsset(meta.id);
      if (!full) throw new Error('Image is missing.');
      const saved = await putAsset(await removeBackground(full));
      onAdd(saved);
      onClose();
    } catch (err) {
      store.toast((err as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Add image"
      hideTitle
      tall
      className="image-sheet"
      header={
        <Segmented<Tab>
          label="Image source"
          value={tab}
          onChange={setTab}
          options={[
            { value: 'photos', label: 'Photos' },
            { value: 'collections', label: 'Collections' },
          ]}
        />
      }
    >
      <div className="img-options">
        <label className="img-cutout">
          <span>
            Cut out background
            <small>Works best on plain backgrounds</small>
          </span>
          <Switch label="Cut out background" checked={cutOut} onChange={setCutOut} />
        </label>
      </div>
      {busy && (
        <p className="img-busy" role="status">
          Preparing image…
        </p>
      )}
      <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => void handleFiles(e.target.files).finally(() => (e.target.value = ''))} />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => void handleFiles(e.target.files).finally(() => (e.target.value = ''))} />
      {tab === 'photos' ? (
        <div className="img-grid">
          <button type="button" className="img-tile-action" onClick={() => fileRef.current?.click()} disabled={busy}>
            <Icon name="image" size={30} />
            <span>Choose photo</span>
          </button>
          <button type="button" className="img-tile-action" onClick={() => cameraRef.current?.click()} disabled={busy}>
            <Icon name="phone" size={30} />
            <span>Take photo</span>
          </button>
          {library.slice(0, 22).map((a) => (
            <Thumb key={a.id} id={a.id} label={`Use ${a.name ?? 'image'}`} onPick={() => void pickExisting(a)} />
          ))}
          <p className="img-hint">You can also paste an image while editing (Ctrl/⌘ + V).</p>
        </div>
      ) : library.length === 0 ? (
        <p className="empty-state">Images you add to notes are collected here so you can reuse them.</p>
      ) : (
        <div className="img-grid">
          {library.map((a) => (
            <Thumb key={a.id} id={a.id} label={`Use ${a.name ?? 'image'}`} onPick={() => void pickExisting(a)} />
          ))}
        </div>
      )}
    </Sheet>
  );
}
